/**
 * Admin Portal → Partners → a tag → Website code (codes platform item C7).
 *
 * "Copy website code": the HTML a partner's developer pastes on their site (a text link,
 * a button, or a button + QR), and the QR on its own as an SVG or PNG download. Every
 * byte is built by `websiteCode.ts` (pure, jest-pinned); this card only picks the link,
 * the variant and the label, previews the result and hands it over.
 *
 * The preview is a sandboxed iframe with NO permissions (no scripts, no navigation, no
 * popups) and pointer events turned off, so an ordinary click in it does nothing. It is
 * not a seal: opening the link from a context menu, dragging it out, or scanning the QR
 * with a phone all reach the real /go page and record a real visit — the card says so.
 *
 * The card hides itself once the tag or its partner has ended (`tagEditable`): an ended
 * link no longer earns, so there is nothing worth pasting.
 *
 * Web-only (the admin SPA).
 */

import React, { useMemo, useState } from 'react';
import { AdminButton, AdminCard } from '../../../components/admin/ui';
import { goLink, tagEditable, type LinkRow, type PartnerStatus, type TagRow } from './partnersLogic';
import { copyText, hint, input, label, row, saveBlob } from './PartnersShared';
import {
  BUTTON_LABEL_MAX,
  DEFAULT_BUTTON_LABEL,
  QR_DARK,
  QR_LIGHT,
  WEBSITE_CODE_VARIANTS,
  WEBSITE_CODE_VARIANT_LABELS,
  buildWebsiteCode,
  capGraphemes,
  checkGoUrl,
  previewDocument,
  qrFileName,
  qrPngPlan,
  qrSvg,
  type WebsiteCodeVariant,
} from './websiteCode';

/**
 * The tag's links a website code may use: its built /go links FOR THIS TAG (a row whose URL
 * parses to another tag is dropped), else the home link. Home first.
 */
export function websiteCodeLinks(tag: Pick<TagRow, 'tag'>, links: Pick<LinkRow, 'destination' | 'url'>[]): { destination: string; url: string }[] {
  const usable = links
    .filter((l) => {
      const checked = checkGoUrl(l.url);
      return checked.ok && checked.tag === tag.tag;
    })
    .map((l) => ({ destination: l.destination, url: l.url }));
  if (!usable.some((l) => l.destination === 'home')) usable.unshift({ destination: 'home', url: goLink(tag.tag) });
  // Home first: it is the default a partner almost always wants.
  return [...usable.filter((l) => l.destination === 'home'), ...usable.filter((l) => l.destination !== 'home')];
}

/**
 * Draw the QR and hand it over as a PNG, SYNCHRONOUSLY inside the click (`toDataURL`, not the
 * async `toBlob`): Safari drops a download started after the user gesture has ended.
 * Returns false when the browser can't (no DOM, no 2D canvas, a refused save).
 */
export function downloadPng(url: string): boolean {
  try {
    if (typeof document === 'undefined' || typeof atob !== 'function') return false;
    const { matrix, scale, px, quiet } = qrPngPlan(url, 1024);
    const canvas = document.createElement('canvas');
    canvas.width = px;
    canvas.height = px;
    const ctx = canvas.getContext('2d');
    if (!ctx || typeof canvas.toDataURL !== 'function') return false;
    ctx.fillStyle = QR_LIGHT;
    ctx.fillRect(0, 0, px, px);
    ctx.fillStyle = QR_DARK;
    matrix.forEach((cells, r) =>
      cells.forEach((dark, c) => {
        if (dark) ctx.fillRect((c + quiet) * scale, (r + quiet) * scale, scale, scale);
      }),
    );
    const dataUrl = canvas.toDataURL('image/png');
    const prefix = 'data:image/png;base64,';
    if (!dataUrl.startsWith(prefix)) return false;
    const bin = atob(dataUrl.slice(prefix.length));
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return saveBlob(new Blob([bytes], { type: 'image/png' }), qrFileName(url, 'png'));
  } catch {
    return false;
  }
}

/** The standalone SVG download; false when the browser can't save it. */
export function downloadSvg(url: string): boolean {
  try {
    return saveBlob(new Blob([qrSvg(url, { size: 1024, standalone: true })], { type: 'image/svg+xml' }), qrFileName(url, 'svg'));
  } catch {
    return false;
  }
}

type CardNotice = { kind: 'ok' | 'error'; text: string } | null;

export const PartnerWebsiteCode: React.FC<{
  tag: TagRow;
  links: LinkRow[];
  partnerStatus: PartnerStatus;
}> = ({ tag, links, partnerStatus }) => {
  const choices = useMemo(() => websiteCodeLinks(tag, links), [tag, links]);
  const [url, setUrl] = useState<string>(choices[0]?.url ?? goLink(tag.tag));
  const [variant, setVariant] = useState<WebsiteCodeVariant>('button_qr');
  const [buttonLabel, setButtonLabel] = useState(DEFAULT_BUTTON_LABEL);
  const [notice, setNotice] = useState<CardNotice>(null);

  const chosen = choices.some((c) => c.url === url) ? url : choices[0]?.url ?? goLink(tag.tag);
  const result = useMemo(() => buildWebsiteCode({ url: chosen, variant, label: buttonLabel }), [chosen, variant, buttonLabel]);
  const inactive = tag.status !== 'active' || partnerStatus !== 'active';

  // An ended tag or partner: nothing to paste (after the hooks — rules of hooks).
  if (!tagEditable(tag.status, partnerStatus)) return null;

  const copyCode = async () => {
    if (!result.ok) return;
    setNotice(
      (await copyText(result.html))
        ? { kind: 'ok', text: 'Copied.' }
        : { kind: 'error', text: 'The browser refused the copy — select the code and copy it by hand.' },
    );
  };

  const saveQr = (kind: 'svg' | 'png') => {
    if (!result.ok) return;
    const saved = kind === 'svg' ? downloadSvg(chosen) : downloadPng(chosen);
    setNotice(saved ? null : { kind: 'error', text: `The browser could not save the ${kind.toUpperCase()} — try the other format, or another browser.` });
  };

  // Any change to what would be copied makes an earlier "Copied." stale.
  const changed = <T,>(set: (v: T) => void) => (v: T) => {
    set(v);
    setNotice(null);
  };

  return (
    <AdminCard>
      <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>Website code</h4>
      <div style={hint}>
        HTML a partner&apos;s developer pastes on their site. Nothing loads from us until a visitor taps: the button and
        the QR both open this tag&apos;s /go link, so the visit is counted on coachcedar.com exactly like the link itself.
      </div>
      {inactive && (
        <div style={{ ...hint, color: 'var(--status-warning-text, #92400e)' }}>
          This {tag.status !== 'active' ? `tag is ${tag.status}` : `partner is ${partnerStatus}`}: its visits are not
          earning while it stays that way.
        </div>
      )}
      <div style={{ ...row, marginTop: 8 }}>
        <div>
          <label style={label} htmlFor={`wc-link-${tag.id}`}>Link</label>
          <select id={`wc-link-${tag.id}`} style={input} value={chosen} onChange={(e) => changed(setUrl)(e.target.value)}>
            {choices.map((c) => <option key={c.url} value={c.url}>{c.destination} — {c.url.replace(/^https:\/\//, '')}</option>)}
          </select>
        </div>
        <div>
          <label style={label} htmlFor={`wc-variant-${tag.id}`}>Style</label>
          <select id={`wc-variant-${tag.id}`} style={input} value={variant} onChange={(e) => changed(setVariant)(e.target.value as WebsiteCodeVariant)}>
            {WEBSITE_CODE_VARIANTS.map((v) => <option key={v} value={v}>{WEBSITE_CODE_VARIANT_LABELS[v]}</option>)}
          </select>
        </div>
        <div>
          <label style={label} htmlFor={`wc-label-${tag.id}`}>Button text</label>
          {/* No maxLength: it counts UTF-16 units, the cap counts user-perceived characters. */}
          <input id={`wc-label-${tag.id}`} style={{ ...input, width: 220 }} value={buttonLabel}
            onChange={(e) => changed(setButtonLabel)(capGraphemes(e.target.value, BUTTON_LABEL_MAX))} />
          <div style={hint}>Up to {BUTTON_LABEL_MAX} characters.</div>
        </div>
      </div>

      {!result.ok && <div style={{ ...hint, color: 'var(--status-error-text, #991b1b)' }}>{result.reason}</div>}
      {result.ok && (
        <>
          <textarea
            aria-label="Website code"
            readOnly
            value={result.html}
            onFocus={(e) => e.currentTarget.select()}
            style={{ ...input, display: 'block', width: '100%', minHeight: 120, marginTop: 12, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 }}
          />
          <div style={{ ...row, marginTop: 8, alignItems: 'center' }}>
            <AdminButton size="sm" onClick={copyCode}>Copy website code</AdminButton>
            <AdminButton size="sm" variant="secondary" onClick={() => saveQr('svg')}>Download QR (SVG)</AdminButton>
            <AdminButton size="sm" variant="secondary" onClick={() => saveQr('png')}>Download QR (PNG)</AdminButton>
            {notice?.kind === 'ok' && <span role="status" style={hint}>{notice.text}</span>}
            {notice?.kind === 'error' && <span role="alert" style={{ ...hint, color: 'var(--status-error-text, #991b1b)' }}>{notice.text}</span>}
          </div>
          <div style={{ ...hint, marginTop: 12 }}>Preview — clicks are turned off here.</div>
          <iframe
            title={`Website code preview for ${tag.tag}`}
            sandbox=""
            referrerPolicy="no-referrer"
            srcDoc={previewDocument(result.html)}
            style={{ width: '100%', height: variant === 'button_qr' ? 200 : 90, border: '1px dashed var(--border-strong, #d1d5db)', borderRadius: 8, background: '#ffffff' }}
          />
          {variant === 'button_qr' && <div style={hint}>Scanning this QR code records a real visit.</div>}
        </>
      )}
    </AdminCard>
  );
};
